import { CharacterData } from "../../../../../utils/game/character/CharacterData";
import { DynamicInventoryDisplayMode } from "../../InventoryItemCollection/InventoryItemDynamicCollection";
import { KeyValuePair } from "../../../../../utils/sys/KeyValuePair";
import { Dictionary } from "../../../../../utils/sys/Dictionary";

import CharacterEquipmentPanel from "../../CharacterEquipmentPanel/CharacterEquipmentPanel";
import CharacterSheetWalletPanel from "../../CharacterSheetWalletPanel/CharacterSheetWalletPanel";
import FlexColumn from "../../../../../common/components/layout/Flexbox/FlexColumn";
import InventoryItemDynamicCollection from "../../InventoryItemCollection/InventoryItemDynamicCollection";
import InventoryItem from "../../../../../utils/game/mechanics/InventoryItem";
import ItemBankModal from "../../../../../common/components/modals/ItemBankModal/ItemBankModal";

import { Key, useCallback, useRef, useState } from "react";
import { UIInventoryItemContainerClickParams } from "../../InventoryItemSlot/InventoryItemContainer";

const stringifyInventory = (items: Array<InventoryItem>) => {
    return JSON.stringify(items.map((item) => item.toJSON()));
};

type Props = {
    characterData: CharacterData;
    isEditable?: boolean;
    inventoryDisplayMode?: DynamicInventoryDisplayMode;
    onInventoryDisplayModeChanged?: (mode: DynamicInventoryDisplayMode) => void;
    onCharacterDataValueChanged?: (vals: KeyValuePair[]) => void;
    equipmentExpanded?: boolean;
    onEquipmentExpandedChanged?: (val: boolean) => void;
    inventoryExpanded?: boolean;
    onInventoryExpandedChanged?: (val: boolean) => void;
    walletExpanded?: boolean;
    onWalletExpandedChanged?: (val: boolean) => void;
};

export default function CharacterSheetInventoryTab(props: Props): JSX.Element {
    const [isItemBankModalVisible, setIsItemBankModalVisible] =
        useState<boolean>(false);
    const [inventoryDisplayMode, setInventoryDisplayMode] =
        useState<DynamicInventoryDisplayMode>(
            props.inventoryDisplayMode ?? "list"
        );

    const pendingItemChanges = useRef(new Dictionary());

    const pendItemChange = (newVal: KeyValuePair) => {
        pendingItemChanges.current.set(newVal.key, newVal.value);
    };

    const executePendingItemChanges = () => {
        const changes = pendingItemChanges.current;

        if (changes.length > 0) {
            props.onCharacterDataValueChanged?.(changes.toArray());
            pendingItemChanges.current.clear();
        }
    };

    return (
        <FlexColumn gap={8}>
            <CharacterEquipmentPanel
                characterData={props.characterData}
                isEditable={props.isEditable}
                onItemChanged={(item, slot) => {
                    pendItemChange({
                        key: slot,
                        value:
                            item === null
                                ? null
                                : JSON.stringify(item.toJSON()),
                    });
                }}
                onTransferSequenceComplete={executePendingItemChanges}
            />
            <button
                onClick={() => {
                    setIsItemBankModalVisible(!isItemBankModalVisible);
                }}
            >
                Item Bank
            </button>
            <ItemBankModal
                isVisible={isItemBankModalVisible}
                onClose={() => {
                    setIsItemBankModalVisible(false);
                }}
            />
            <InventoryItemDynamicCollection
                id={`character-inventory-${props.characterData.id}`}
                label="Inventory"
                items={props.characterData.inventory}
                isEditable={props.isEditable}
                displayMode={inventoryDisplayMode}
                onDisplayModeChanged={(mode) => {
                    setInventoryDisplayMode(() => {
                        props.onInventoryDisplayModeChanged?.(mode);
                        return mode;
                    });
                }}
                expanded={props.inventoryExpanded}
                onExpandedChanged={props.onInventoryExpandedChanged}
                onItemChanged={(newItem, newItemIndex, mode, isInSequence) => {
                    if (newItem == null && mode === "insert") {
                        return;
                    }

                    let updatedItems = [...props.characterData.inventory];

                    if (mode === "insert") {
                        if (newItem == null) {
                            return;
                        } else {
                            updatedItems.splice(newItemIndex, 0, newItem);
                        }
                    } else if (mode === "replace") {
                        if (newItem == null) {
                            updatedItems = updatedItems.filter(
                                (_, index) => index !== newItemIndex
                            );
                        } else {
                            updatedItems = updatedItems.map((item, index) =>
                                index === newItemIndex ? newItem : item
                            );
                        }
                    }

                    const data = {
                        key: "inventory",
                        value: JSON.stringify(
                            updatedItems.map((item) => item.toJSON())
                        ),
                    };

                    if (isInSequence) {
                        pendItemChange(data);
                    } else {
                        props.onCharacterDataValueChanged?.([data]);
                    }
                }}
                onTransferSequenceComplete={executePendingItemChanges}
                onItemsReordered={(reorderedItems) => {
                    props.onCharacterDataValueChanged?.([
                        {
                            key: "inventory",
                            value: JSON.stringify(
                                reorderedItems.map((item) => item.toJSON())
                            ),
                        },
                    ]);
                }}
            />
            <CharacterSheetWalletPanel
                isEditable={props.isEditable}
                coinCounts={props.characterData.wallet}
                onCoinCountChanged={(newCoinCounts) => {
                    props.onCharacterDataValueChanged?.([
                        {
                            key: "wallet",
                            value: newCoinCounts.map((c) => {
                                return `[${c.coin.id}]${c.count}[/${c.coin.id}]`;
                            }),
                        },
                    ]);
                }}
                isExpanded={props.walletExpanded}
                onExpandedChanged={props.onWalletExpandedChanged}
            />
        </FlexColumn>
    );
}
